/**
 * ComparisonMap — all selected sites on one MapLibre map with lettered
 * markers (A/B/C…), trade-area rings, and overlap zones rendered when
 * rings intersect.
 *
 * Phase I components (`OverlapZones`, `SiteMarkers`, `MapLegend`,
 * `MapControls`, `DemographicHeatmap`) are now standalone in
 * `features/Map`. F6 was originally shipped with inline copies of the
 * I1–I4 logic so it didn't block on Phase I; the closing-sweep
 * (2026-05-02) swapped those inlines for the standalone components.
 *
 * Difference vs the full-page `EvaluationMap` (I5): this component
 * renders only the picker subset, not every site in the evaluation.
 *
 * The map auto-fits to the bbox of all selected sites whenever the
 * picker selection changes (`tradeAreaService.getBoundsForSites`).
 *
 * Sites with missing lat/lng are listed in a banner above the map and
 * skipped from the visible features.
 *
 * Linear: MYD-221 (F6) + closing-sweep refactor.
 */

import { useEffect, useMemo, useState } from 'react'
import type maplibregl from 'maplibre-gl'
import { Card } from 'components/ui/card'
import { AlertCircle, Map as MapIcon } from 'lucide-react'

import {
	MapContainer,
	TradeAreaRings,
	OverlapZones,
	SiteMarkers,
	MapLegend,
	MapControls,
	tradeAreaService,
	TRADE_AREA_RINGS,
	type SiteForRings,
	type RingVisibilityConfig,
	type SiteForOverlap,
} from 'features/Map'

interface ComparisonMapProps {
	sites: any[]
}

const ComparisonMap = ({ sites }: ComparisonMapProps) => {
	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)

	const [showMarkers, setShowMarkers] = useState(true)
	const [showRings, setShowRings] = useState(true)
	const [showOverlaps, setShowOverlaps] = useState(true)

	const geocodedSites = useMemo(
		() =>
			sites.filter(
				(s) =>
					typeof s.latitude === 'number' &&
					typeof s.longitude === 'number' &&
					Number.isFinite(s.latitude) &&
					Number.isFinite(s.longitude),
			),
		[sites],
	)
	const ungeocodedCount = sites.length - geocodedSites.length

	const ringSites: SiteForRings[] = useMemo(
		() =>
			geocodedSites.map((s) => ({
				id: s._id,
				latitude: s.latitude,
				longitude: s.longitude,
			})),
		[geocodedSites],
	)
	const overlapSites: SiteForOverlap[] = ringSites
	const ringVisibility: RingVisibilityConfig = useMemo(() => {
		const out: RingVisibilityConfig = {}
		for (const s of geocodedSites) {
			out[s._id] = TRADE_AREA_RINGS as unknown as number[]
		}
		return out
	}, [geocodedSites])

	// Auto-fit on selection change.
	useEffect(() => {
		if (!map || !isLoaded || geocodedSites.length === 0) return
		const bounds = tradeAreaService.getBoundsForSites(
			geocodedSites.map((s) => ({ longitude: s.longitude, latitude: s.latitude })),
		)
		map.fitBounds(bounds as maplibregl.LngLatBoundsLike, {
			padding: 60,
			duration: 600,
			maxZoom: 13,
		})
	}, [map, isLoaded, geocodedSites])

	if (geocodedSites.length === 0) {
		return (
			<Card className="p-8 text-center">
				<MapIcon className="w-8 h-8 mx-auto text-gray-300 mb-3" />
				<h4 className="text-sm font-semibold text-gray-700 mb-1">No mappable sites yet</h4>
				<p className="text-xs text-gray-500">
					Sites need a successful geocode before they appear on the comparison map. Add or
					update site addresses in the Sites tab.
				</p>
			</Card>
		)
	}

	return (
		<div className="space-y-3">
			{/* Toggle controls — I3 MapControls component */}
			<div className="flex flex-wrap items-center gap-2">
				<MapControls
					controls={[
						{
							key: 'markers',
							label: 'Markers',
							active: showMarkers,
							onToggle: () => setShowMarkers((v) => !v),
						},
						{
							key: 'rings',
							label: 'Rings',
							active: showRings,
							onToggle: () => setShowRings((v) => !v),
						},
						{
							key: 'overlaps',
							label: 'Overlaps',
							active: showOverlaps,
							onToggle: () => setShowOverlaps((v) => !v),
						},
					]}
				/>
				{ungeocodedCount > 0 && (
					<span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
						<AlertCircle className="w-3 h-3" />
						{ungeocodedCount} site{ungeocodedCount === 1 ? '' : 's'} not geocoded —
						hidden
					</span>
				)}
			</div>

			{/* Map */}
			<div className="relative w-full h-[480px] rounded-md border border-gray-200 overflow-hidden">
				<MapContainer
					onMapLoad={(m) => {
						setMap(m)
						setIsLoaded(true)
					}}
				>
					{showRings && (
						<TradeAreaRings
							map={map}
							isMapLoaded={isLoaded}
							sites={ringSites}
							visibility={ringVisibility}
							visible={showRings}
						/>
					)}
					<OverlapZones
						map={map}
						isMapLoaded={isLoaded}
						sites={overlapSites}
						visible={showOverlaps}
					/>
					{showMarkers && <SiteMarkers map={map} sites={geocodedSites} />}
				</MapContainer>
			</div>

			{/* Legend — I3 MapLegend component */}
			<MapLegend sites={geocodedSites} title="Sites" />
		</div>
	)
}

export default ComparisonMap
